import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { useCCPOPRPSievesContext, CCP_SCREENS } from '../context/CCPOPRPSievesContext';
import type { CompletedCycleRecord } from '../context/CCPOPRPSievesContext';
import { ccpOprpSievesService } from '../services/ccpOprpSievesService';
import { getSavedPhotoNames, saveCyclePhotos } from '../services/ccpPhotoStore';
import { LINE_CHECKPOINTS_MAP } from '../constants/ccpOprpSievesData';
import { SP_MSAL_SCOPE } from '../../../../constants/sharepoint';
import PhotoAttachField from './PhotoAttachField';
import type { AttachedPhoto } from './PhotoAttachField';
import moment from 'moment';

interface SubCheckState {
    criteria: "Okay" | "Not Okay";
    sensitivityValue: string;
    defectRemarks: string;
    dbId?: string;
}

interface TabRowState {
    rowNumber: number;
    subChecks: Record<string, SubCheckState>;
}

const DEFAULT_SUB_CHECKS: { label: string; key: string; group: string; hasValue: boolean }[] = [
    { label: "FE - Centre 1st Pass", key: "fecentrepass1", group: "FE (Ferrous) Check", hasValue: false },
    { label: "FE - Centre 2nd Pass", key: "fecentrepass2", group: "FE (Ferrous) Check", hasValue: false },
    { label: "NFE - Centre 1st Pass", key: "nfecentrepass1", group: "NFE (Non-Ferrous) Check", hasValue: false },
    { label: "NFE - Centre 2nd Pass", key: "nfecentrepass2", group: "NFE (Non-Ferrous) Check", hasValue: false },
    { label: "SS - Centre 1st Pass", key: "sscentrepass1", group: "SS (Stainless Steel) Check", hasValue: false },
    { label: "SS - Centre 2nd Pass", key: "sscentrepass2", group: "SS (Stainless Steel) Check", hasValue: false },
    { label: "MD Sensitivity Check", key: "mdsensitivity", group: "M.D. Sensitivity & Rejection in Time", hasValue: true }
];

const CCPOPRPChecklist: React.FC = () => {
    const { state, dispatch } = useCCPOPRPSievesContext();
    const { instance, accounts } = useMsal();
    const session = state.sessionData;
    const line = session?.line || "Line-1";

    const [currentCycle, setCurrentCycle] = useState(state.currentCycle || "Cycle-1");
    const [acceptanceResponse, setAcceptanceResponse] = useState<"OK" | "Line Not Operational" | "Plant Not Operational">("OK");
    const [shutdownReason, setShutdownReason] = useState("");
    const [productName, setProductName] = useState(session?.productVariety || "Speciality Sauces");
    const [executiveName, setExecutiveName] = useState(session?.productionIncharge || "Production Incharge (Rajpura)");
    const [sessionTime, setSessionTime] = useState(moment().format("HH:mm"));

    useEffect(() => {
        if (session?.productionIncharge) {
            setExecutiveName(session.productionIncharge);
        }
        if (session?.productVariety) {
            setProductName(session.productVariety);
        }
    }, [session]);

    const [isSessionStarted, setIsSessionStarted] = useState(false);
    const [activeTabIdx, setActiveTabIdx] = useState(0);
    const [tabRowsMap, setTabRowsMap] = useState<Record<number, TabRowState[]>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [expandedCycle, setExpandedCycle] = useState<string | null>(null);
    const [photos, setPhotos] = useState<AttachedPhoto[]>([]);

    // Shift modal
    const [isShiftModalOpen, setIsShiftModalOpen] = useState(false);
    const [selectedShiftModal, setSelectedShiftModal] = useState(session?.shift || state.selectedShift || "Shift-1");

    // Dynamic inputs in completed cycles
    const [actionPlans, setActionPlans] = useState<Record<string, string>>({});
    const [qaComments, setQaComments] = useState<Record<string, string>>({});

    const checkpoints = LINE_CHECKPOINTS_MAP[line] || ["CCP (Metal Detector)"];

    // Load all completed cycles from Dataverse/offline if not loaded or tour changes
    useEffect(() => {
        if (!state.currentTourId) return;

        const loadCycles = async () => {
            try {
                const fetchedCheckpoints = await ccpOprpSievesService.getCCPOPRPCheckpoints(state.currentTourId!);
                if (fetchedCheckpoints && fetchedCheckpoints.length > 0) {
                    const cycleGroups: Record<string, any[]> = {};
                    fetchedCheckpoints.forEach((cp: any) => {
                        const cName = cp.cr3ea_cycle || "Cycle-1";
                        if (!cycleGroups[cName]) cycleGroups[cName] = [];
                        cycleGroups[cName].push(cp);
                    });

                    const sortedCycleKeys = Object.keys(cycleGroups).sort((a, b) => {
                        const numA = parseInt(a.replace(/\D/g, ""), 10) || 0;
                        const numB = parseInt(b.replace(/\D/g, ""), 10) || 0;
                        return numA - numB;
                    });

                    const completedRecords: CompletedCycleRecord[] = sortedCycleKeys.map(cName => {
                        const rows = cycleGroups[cName];
                        const first = rows[0];
                        const formattedRows = rows.map((r: any) => ({
                            dbId: r.cr3ea_prod_rajpura_ccpoprpid,
                            checkpointName: r.cr3ea_checkpointname || "",
                            description: r.cr3ea_checkpointdescription || "",
                            category: r.cr3ea_category || "OPRP",
                            criteria: r.cr3ea_criteria || "Okay",
                            mdSensitivityValue: r.cr3ea_mdsensitivityvalue,
                            defectRemarks: r.cr3ea_defectremarks,
                            actionTaken: r.cr3ea_actiontaken,
                            notifiedDepartment: r.cr3ea_notifieddepartment,
                            deviationStatus: r.cr3ea_deviationstatus,
                            escalated: r.cr3ea_escalated
                        }));

                        const total = formattedRows.length;
                        const ok = formattedRows.filter((f: any) => f.criteria === "Okay").length;
                        const score = total > 0 ? Math.round((ok / total) * 100) : 100;
                        const hasDev = formattedRows.some((f: any) => f.criteria === "Not Okay");

                        let recStatus: CompletedCycleRecord['status'] = "Submitted";
                        if (first?.cr3ea_acceptanceresponse && first.cr3ea_acceptanceresponse !== "OK") {
                            recStatus = "Not Operational";
                        } else if (hasDev) {
                            const allClosed = formattedRows.filter((f: any) => f.criteria === "Not Okay").every((f: any) => f.deviationStatus === "Closed");
                            recStatus = allClosed ? "Completed" : "Pending Production Action";
                        } else {
                            recStatus = "Completed";
                        }

                        return {
                            cycle: cName,
                            cycleDate: first?.cr3ea_tourstartdate || new Date().toISOString(),
                            productName: first?.cr3ea_productname || session?.productVariety || "Speciality Sauces",
                            executiveName: first?.cr3ea_executivename || session?.productionIncharge || "Production Incharge",
                            location: first?.cr3ea_location || line,
                            acceptanceResponse: first?.cr3ea_acceptanceresponse || "OK",
                            acceptanceReason: first?.cr3ea_acceptanceresponse !== "OK" ? first?.cr3ea_defectremarks : undefined,
                            checkpoints: formattedRows,
                            score,
                            hasDeviations: hasDev,
                            status: recStatus
                        };
                    });

                    const savedPhotos = await getSavedPhotoNames(state.currentTourId!, 'CCP_OPRP');
                    completedRecords.forEach(record => {
                        if (!record.photoFileNames?.length && savedPhotos[record.cycle]) {
                            record.photoFileNames = savedPhotos[record.cycle];
                        }
                    });

                    dispatch({ type: 'SET_COMPLETED_CYCLES', payload: completedRecords });
                    const maxCycleNum = sortedCycleKeys.reduce((max, k) => {
                        const num = parseInt(k.replace(/\D/g, ""), 10) || 0;
                        return num > max ? num : max;
                    }, 0);
                    const nextCycleNumber = maxCycleNum + 1;
                    setCurrentCycle(`Cycle-${nextCycleNumber}`);
                    dispatch({ type: 'SET_CURRENT_CYCLE', payload: `Cycle-${nextCycleNumber}` });
                }
            } catch (err) {
                console.warn("Could not load cycle history in CCPOPRPChecklist:", err);
            }
        };

        loadCycles();
    }, [state.currentTourId]);

    // Initialize tab rows
    useEffect(() => {
        const initialMap: Record<number, TabRowState[]> = {};
        checkpoints.forEach((_, idx) => {
            const initialSubChecks: Record<string, SubCheckState> = {};
            DEFAULT_SUB_CHECKS.forEach(sc => {
                initialSubChecks[sc.key] = {
                    criteria: "Okay",
                    sensitivityValue: sc.hasValue ? "Fe: 1.5mm / NFe: 1.5mm / SS: 2.0mm" : "",
                    defectRemarks: ""
                };
            });

            initialMap[idx] = [
                {
                    rowNumber: 1,
                    subChecks: initialSubChecks
                }
            ];
        });
        setTabRowsMap(initialMap);
        setIsSessionStarted(false);
        setActiveTabIdx(0);
        setPhotos(prev => {
            prev.forEach(photo => URL.revokeObjectURL(photo.previewUrl));
            return [];
        });
    }, [line, currentCycle]);

    const handleAddRow = (tabIdx: number) => {
        setTabRowsMap(prev => {
            const currentRows = prev[tabIdx] || [];
            const nextRowNumber = currentRows.length + 1;

            const initialSubChecks: Record<string, SubCheckState> = {};
            DEFAULT_SUB_CHECKS.forEach(sc => {
                initialSubChecks[sc.key] = {
                    criteria: "Okay",
                    sensitivityValue: sc.hasValue ? "Fe: 1.5mm / NFe: 1.5mm / SS: 2.0mm" : "",
                    defectRemarks: ""
                };
            });

            return {
                ...prev,
                [tabIdx]: [
                    ...currentRows,
                    {
                        rowNumber: nextRowNumber,
                        subChecks: initialSubChecks
                    }
                ]
            };
        });
    };

    const handleRemoveRow = (tabIdx: number, rowIdx: number) => {
        setTabRowsMap(prev => {
            const currentRows = prev[tabIdx] || [];
            const filtered = currentRows.filter((_, idx) => idx !== rowIdx);
            const reindexed = filtered.map((r, i) => ({ ...r, rowNumber: i + 1 }));
            return { ...prev, [tabIdx]: reindexed };
        });
    };

    const handleSubCheckChange = (
        tabIdx: number,
        rowIdx: number,
        key: string,
        updates: Partial<SubCheckState>
    ) => {
        setTabRowsMap(prev => {
            const currentRows = [...(prev[tabIdx] || [])];
            const targetRow = { ...currentRows[rowIdx] };
            const currentSubChecks = { ...targetRow.subChecks };
            currentSubChecks[key] = { ...currentSubChecks[key], ...updates };
            targetRow.subChecks = currentSubChecks;
            currentRows[rowIdx] = targetRow;
            return { ...prev, [tabIdx]: currentRows };
        });
    };

    const getNextCycleName = (cycleStr: string) => {
        const match = cycleStr.match(/\d+/);
        const num = match ? parseInt(match[0], 10) : 1;
        return `Cycle-${num + 1}`;
    };

    // Save Shutdown cycle
    const handleSaveShutdown = async () => {
        if (!state.currentTourId || !session) return;
        if (!shutdownReason.trim()) {
            alert("Please provide the reason for closure/shutdown.");
            return;
        }

        setIsSubmitting(true);
        try {
            await ccpOprpSievesService.cleanChecklistItems(state.currentTourId, "CCP", currentCycle);
            await ccpOprpSievesService.saveCCPOPRPCycle(
                state.currentTourId,
                currentCycle,
                [],
                {
                    ...session,
                    productVariety: productName,
                    productionIncharge: executiveName
                },
                acceptanceResponse,
                shutdownReason
            );

            const completedRecord: CompletedCycleRecord = {
                cycle: currentCycle,
                cycleDate: new Date().toISOString(),
                sessionTime,
                productName: "N/A",
                executiveName,
                location: session.line || "Line-1",
                acceptanceResponse,
                acceptanceReason: shutdownReason,
                checkpoints: [],
                score: 100,
                hasDeviations: false,
                status: "Not Operational",
                isPaused: false
            };

            dispatch({ type: 'ADD_COMPLETED_CYCLE', payload: completedRecord });
            const nextCycle = getNextCycleName(currentCycle);
            setCurrentCycle(nextCycle);
            dispatch({ type: 'SET_CURRENT_CYCLE', payload: nextCycle });
            setIsSessionStarted(false);
            setShutdownReason("");
            setAcceptanceResponse("OK");
        } catch (err) {
            console.error("Failed to save shutdown session:", err);
            alert("Failed to record shutdown closure.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Save Active Checklist Session
    const handleSaveSession = async (isPause: boolean = false) => {
        if (!state.currentTourId || !session) {
            alert("No active tour found.");
            return;
        }

        // Validate not-okay defect remarks
        if (!isPause) {
            for (let t = 0; t < checkpoints.length; t++) {
                const rows = tabRowsMap[t] || [];
                for (const row of rows) {
                    for (const sc of DEFAULT_SUB_CHECKS) {
                        const checkState = row.subChecks[sc.key];
                        if (checkState && checkState.criteria === "Not Okay" && !checkState.defectRemarks?.trim()) {
                            alert(`Please provide Defect Remarks for: ${checkpoints[t]} (Row ${row.rowNumber}) - ${sc.label}`);
                            return;
                        }
                    }
                }
            }
        }

        setIsSubmitting(true);
        try {
            const flattenedRows: any[] = [];
            checkpoints.forEach((cpName, tIdx) => {
                const rows = tabRowsMap[tIdx] || [];
                rows.forEach(r => {
                    DEFAULT_SUB_CHECKS.forEach(sc => {
                        const checkData = r.subChecks[sc.key] || {
                            criteria: "Okay",
                            sensitivityValue: "",
                            defectRemarks: ""
                        };

                        flattenedRows.push({
                            checkpointName: `${cpName} - Row ${r.rowNumber} - ${sc.label}`,
                            description: `${cpName} (${sc.label})`,
                            category: cpName.includes("CCP") ? "CCP" : "OPRP",
                            tabName: cpName,
                            rowNumber: r.rowNumber,
                            groupTitle: sc.group,
                            subCheckName: sc.label,
                            criteria: checkData.criteria,
                            mdSensitivityValue: checkData.sensitivityValue,
                            defectRemarks: checkData.defectRemarks,
                            notifiedDepartment: "Production",
                            deviationStatus: checkData.criteria === "Not Okay" ? "New" : "None",
                            dbId: checkData.dbId
                        });
                    });
                });
            });

            let photoFileNames: string[] = [];
            if (photos.length > 0) {
                let accessToken: string | undefined;
                try {
                    if (accounts[0]) {
                        const tokenResponse = await instance.acquireTokenSilent({
                            scopes: [SP_MSAL_SCOPE],
                            account: accounts[0],
                        });
                        accessToken = tokenResponse.accessToken;
                    }
                } catch (tokenError) {
                    console.warn('SharePoint token unavailable for CCP/OPRP photos:', tokenError);
                }
                photoFileNames = await saveCyclePhotos({
                    tourId: state.currentTourId,
                    cycle: currentCycle,
                    section: 'CCP_OPRP',
                    files: photos.map(photo => photo.file),
                    accessToken,
                });
            }

            await ccpOprpSievesService.cleanChecklistItems(state.currentTourId, "CCP", currentCycle);
            await ccpOprpSievesService.saveCCPOPRPCycle(
                state.currentTourId,
                currentCycle,
                flattenedRows,
                {
                    ...session,
                    productVariety: productName,
                    productionIncharge: executiveName
                },
                "OK",
                undefined,
                isPause
            );

            const total = flattenedRows.length;
            const ok = flattenedRows.filter(f => f.criteria === "Okay").length;
            const score = total > 0 ? Math.round((ok / total) * 100) : 100;
            const hasDeviations = flattenedRows.some(f => f.criteria === "Not Okay");

            let status: CompletedCycleRecord['status'] = "Completed";
            if (isPause) {
                status = "Checklist Filling - Paused";
            } else if (hasDeviations) {
                status = "Pending Production Action";
            }

            const completedRecord: CompletedCycleRecord = {
                cycle: currentCycle,
                cycleDate: new Date().toISOString(),
                sessionTime,
                productName,
                executiveName,
                location: session.line || "Line-1",
                acceptanceResponse: "OK",
                checkpoints: flattenedRows,
                score,
                hasDeviations,
                photoFileNames,
                status,
                isPaused: isPause
            };

            dispatch({ type: 'ADD_COMPLETED_CYCLE', payload: completedRecord });

            if (!isPause) {
                const nextCycle = getNextCycleName(currentCycle);
                setCurrentCycle(nextCycle);
                dispatch({ type: 'SET_CURRENT_CYCLE', payload: nextCycle });
                setIsSessionStarted(false);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                alert("Session paused successfully. You can resume anytime.");
            }
        } catch (err) {
            console.error("Failed to save session:", err);
            alert("Failed to save CCP & OPRP session.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Corrective Action Submission (Production)
    const handleSubmitCorrectiveActions = async (cycleName: string, cycleRecord: CompletedCycleRecord) => {
        if (!state.currentTourId) return;

        const updatedCheckpoints = (cycleRecord.checkpoints || []).map(cp => {
            const key = `${cycleName}_${cp.checkpointName}`;
            const action = actionPlans[key] || cp.actionTaken || "Action implemented by production";
            if (cp.criteria === "Not Okay" && cp.deviationStatus !== "Closed") {
                return {
                    ...cp,
                    actionTaken: action,
                    deviationStatus: "Action Taken" as const
                };
            }
            return cp;
        });

        setIsSubmitting(true);
        try {
            for (const cp of updatedCheckpoints) {
                if (cp.criteria === "Not Okay" && cp.dbId) {
                    await ccpOprpSievesService.updateDeviationStatus(cp.dbId, "CCP", {
                        cr3ea_actiontaken: cp.actionTaken,
                        cr3ea_deviationstatus: "Action Taken"
                    });
                }
            }

            dispatch({
                type: 'UPDATE_COMPLETED_CYCLE',
                payload: {
                    cycle: cycleName,
                    record: {
                        checkpoints: updatedCheckpoints,
                        status: "Pending QA Re-Verification"
                    }
                }
            });
            alert("Corrective actions submitted to QA for verification.");
        } catch (err) {
            console.error("Failed to submit corrective actions:", err);
            alert("Failed to update corrective actions.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // QA Verification Closure or Rejection
    const handleQAClosure = async (cycleName: string, cycleRecord: CompletedCycleRecord, isApprove: boolean) => {
        if (!state.currentTourId) return;

        const updatedCheckpoints = (cycleRecord.checkpoints || []).map(cp => {
            const key = `${cycleName}_${cp.checkpointName}`;
            const comment = qaComments[key] || (isApprove ? "Verified and approved by QA" : "Rejected by QA");
            if (cp.criteria === "Not Okay") {
                if (isApprove) {
                    return {
                        ...cp,
                        defectRemarks: `${cp.defectRemarks || ''} | Re-verified: ${comment}`,
                        deviationStatus: "Closed" as const
                    };
                } else {
                    return {
                        ...cp,
                        deviationStatus: "New" as const
                    };
                }
            }
            return cp;
        });

        setIsSubmitting(true);
        try {
            for (const cp of updatedCheckpoints) {
                if (cp.criteria === "Not Okay" && cp.dbId) {
                    await ccpOprpSievesService.updateDeviationStatus(cp.dbId, "CCP", {
                        cr3ea_deviationstatus: isApprove ? "Closed" : "New",
                        cr3ea_defectremarks: cp.defectRemarks
                    });
                }
            }

            const allResolved = !updatedCheckpoints.some(cp => cp.criteria === "Not Okay" && cp.deviationStatus !== "Closed");

            dispatch({
                type: 'UPDATE_COMPLETED_CYCLE',
                payload: {
                    cycle: cycleName,
                    record: {
                        checkpoints: updatedCheckpoints,
                        status: isApprove && allResolved ? "Completed" : "Pending Production Action"
                    }
                }
            });
            alert(isApprove ? "Cycle verified and closed successfully." : "Actions rejected. Production notified to resubmit.");
        } catch (err) {
            console.error("QA Closure update failed:", err);
            alert("Failed to update QA verification.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Stepper component generator
    const renderStepper = (status: string) => {
        const steps = [
            { id: 1, label: "Setup Info" },
            { id: 2, label: "Checklist Fill" },
            { id: 3, label: "Action Plan" },
            { id: 4, label: "Re-Verify" },
            { id: 5, label: "Closed" }
        ];

        let activeIdx = 0;
        if (status === "Metadata Setup") activeIdx = 0;
        else if (status === "Checklist Filling" || status === "Checklist Filling - Paused") activeIdx = 1;
        else if (status === "Pending Production Action") activeIdx = 2;
        else if (status === "Pending QA Re-Verification") activeIdx = 3;
        else if (status === "Completed" || status === "Not Operational" || status === "Escalated") activeIdx = 4;

        return (
            <div className="tour-workflow-stepper">
                {steps.map((step, idx) => {
                    const isCompleted = idx < activeIdx;
                    const isActive = idx === activeIdx;

                    return (
                        <React.Fragment key={step.id}>
                            {isCompleted ? (
                                <div className="step-item completed">
                                    <span style={{ fontWeight: 'bold' }}>✓</span>
                                    <span>{step.label}</span>
                                </div>
                            ) : isActive ? (
                                <div className="step-item active">
                                    <span>{step.id}.</span>
                                    <span>{step.label}</span>
                                </div>
                            ) : (
                                <div className="step-item upcoming">
                                    <span>{step.id}.</span>
                                    <span>{step.label}</span>
                                </div>
                            )}
                            {idx < steps.length - 1 && (
                                <div className="step-divider">➔</div>
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        );
    };

    // Condensed Matrix Summary Table for Completed Cycles
    const renderMatrixSummaryTable = (checkpointsList: any[]) => {
        const map: Record<string, {
            name: string;
            fe1: string;
            fe2: string;
            nfe1: string;
            nfe2: string;
            ss1: string;
            ss2: string;
            mdSens: string;
            mdSensVal: string;
        }> = {};

        checkpointsList.forEach(cp => {
            const full = cp.checkpointName || "";
            const parts = full.split(" - ");
            const parentName = parts[0] || "Checkpoint";

            let mapKey = parentName;
            let subLabel = "";
            if (parts.length >= 3) {
                mapKey = `${parentName} - ${parts[1]}`;
                subLabel = parts.slice(2).join(" - ");
            } else {
                mapKey = `${parentName} - Row 1`;
                subLabel = parts.slice(1).join(" - ");
            }

            if (!map[mapKey]) {
                map[mapKey] = {
                    name: mapKey,
                    fe1: "OK",
                    fe2: "OK",
                    nfe1: "OK",
                    nfe2: "OK",
                    ss1: "OK",
                    ss2: "OK",
                    mdSens: "OK",
                    mdSensVal: "N/A"
                };
            }

            let val = cp.criteria === "Okay" ? "OK" : "Not Okay";
            if (cp.deviationStatus === "Closed") {
                val = "OK";
            }
            const labelLower = subLabel.toLowerCase();

            if (labelLower.includes("fe") && !labelLower.includes("nfe") && labelLower.includes("1st")) map[mapKey].fe1 = val;
            else if (labelLower.includes("fe") && !labelLower.includes("nfe") && labelLower.includes("2nd")) map[mapKey].fe2 = val;
            else if (labelLower.includes("nfe") && labelLower.includes("1st")) map[mapKey].nfe1 = val;
            else if (labelLower.includes("nfe") && labelLower.includes("2nd")) map[mapKey].nfe2 = val;
            else if (labelLower.includes("ss") && labelLower.includes("1st")) map[mapKey].ss1 = val;
            else if (labelLower.includes("ss") && labelLower.includes("2nd")) map[mapKey].ss2 = val;
            else if (labelLower.includes("md") || labelLower.includes("sensitivity")) {
                map[mapKey].mdSens = val;
                map[mapKey].mdSensVal = cp.mdSensitivityValue || "N/A";
            }
        });

        const rows = Object.values(map);

        return (
            <div className="bs-table-container">
                <table className="bs-table">
                    <thead>
                        <tr>
                            <th>Checkpoint</th>
                            <th style={{ textAlign: 'center' }}>FE-1</th>
                            <th style={{ textAlign: 'center' }}>FE-2</th>
                            <th style={{ textAlign: 'center' }}>NFE-1</th>
                            <th style={{ textAlign: 'center' }}>NFE-2</th>
                            <th style={{ textAlign: 'center' }}>SS-1</th>
                            <th style={{ textAlign: 'center' }}>SS-2</th>
                            <th style={{ textAlign: 'center' }}>MD Sens</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={i}>
                                <td style={{ fontWeight: 600 }}>{r.name}</td>
                                <td style={{ textAlign: 'center' }} className={r.fe1 === 'OK' ? 'ok-text' : 'not-ok-text'}>{r.fe1}</td>
                                <td style={{ textAlign: 'center' }} className={r.fe2 === 'OK' ? 'ok-text' : 'not-ok-text'}>{r.fe2}</td>
                                <td style={{ textAlign: 'center' }} className={r.nfe1 === 'OK' ? 'ok-text' : 'not-ok-text'}>{r.nfe1}</td>
                                <td style={{ textAlign: 'center' }} className={r.nfe2 === 'OK' ? 'ok-text' : 'not-ok-text'}>{r.nfe2}</td>
                                <td style={{ textAlign: 'center' }} className={r.ss1 === 'OK' ? 'ok-text' : 'not-ok-text'}>{r.ss1}</td>
                                <td style={{ textAlign: 'center' }} className={r.ss2 === 'OK' ? 'ok-text' : 'not-ok-text'}>{r.ss2}</td>
                                <td style={{ textAlign: 'center' }} className={r.mdSens === 'OK' ? 'ok-text' : 'not-ok-text'}>
                                    {r.mdSensVal !== "N/A" ? r.mdSensVal : r.mdSens}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const handleShiftChange = () => {
        dispatch({
            type: 'SET_SELECTIONS',
            payload: {
                parameterVerification: "CCP & OPRP",
                shift: selectedShiftModal,
                frequency: state.selectedFrequency
            }
        });
        if (session) {
            dispatch({
                type: 'SET_SESSION_DATA',
                payload: { ...session, shift: selectedShiftModal }
            });
        }
        setIsShiftModalOpen(false);
    };

    const activeRows = tabRowsMap[activeTabIdx] || [];

    return (
        <div id="rajpuraQualityDashboard">
            <main className="plan-tour-content">
                <div id="checklist-main-container">
                    {/* 1. Header with Shift Switcher */}
                    <div className="plan-tour-header">
                        <div className="tour-header-info-wrapper">
                            <h3 className="tour-header-title">CCP, OPRP & Sieves/Magnets (Rajpura)</h3>
                            <div className="tour-header-meta">
                                <span className="badge" id="shiftBadge">{session?.shift || state.selectedShift || "Shift 1"}</span>
                                <span className="tour-date" id="currentDay">{moment().format("DD/MM/YYYY")}</span>
                            </div>
                            <div className="bs-menu-parent">
                                <button
                                    type="button"
                                    className="bs-btn icon-btn bs-menu-opener"
                                    onClick={() => setIsShiftModalOpen(true)}
                                    title="Change Shift"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                        <path d="M7.56043 9.00004C7.56043 9.79533 8.20514 10.44 9.00043 10.44C9.79572 10.44 10.4404 9.79533 10.4404 9.00004C10.4404 8.20475 9.79572 7.56004 9.00043 7.56004C8.20514 7.56004 7.56043 8.20475 7.56043 9.00004Z" fill="#0C0D10" />
                                        <path d="M7.56043 13.59C7.56043 14.3853 8.20514 15.03 9.00043 15.03C9.79572 15.03 10.4404 14.3853 10.4404 13.59C10.4404 12.7947 9.79572 12.15 9.00043 12.15C8.20514 12.15 7.56043 12.7947 7.56043 13.59Z" fill="#0C0D10" />
                                        <path d="M7.56043 4.41002C7.56043 5.20531 8.20514 5.85002 9.00043 5.85002C9.79572 5.85002 10.4404 5.20531 10.4404 4.41002C10.4404 3.61473 9.79572 2.97002 9.00043 2.97002C8.20514 2.97002 7.56043 3.61473 7.56043 4.41002Z" fill="#0C0D10" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Shift Modal */}
                    {isShiftModalOpen && (
                        <div className="main-popup is-popup-active">
                            <div className="popup-bg" onClick={() => setIsShiftModalOpen(false)}></div>
                            <div className="popup-content">
                                <div className="popup-common-box">
                                    <div className="popup-header">
                                        <h4 className="popup-header-title">Select your shift</h4>
                                        <button type="button" className="bs-btn popup-close-btn" onClick={() => setIsShiftModalOpen(false)}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                <path d="M11 1L1 11M1 1L11 11" stroke="#6D757E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </button>
                                    </div>
                                    <div className="popup-body">
                                        <label className="form-label">Shift</label>
                                        <select
                                            className="form-select"
                                            value={selectedShiftModal}
                                            onChange={e => setSelectedShiftModal(e.target.value)}
                                        >
                                            <option value="Shift-1">Shift 1</option>
                                            <option value="Shift-2">Shift 2</option>
                                            <option value="Shift-3">Shift 3</option>
                                        </select>
                                    </div>
                                    <div className="popup-footer">
                                        <button type="button" className="bs-btn bs-btn-outline-primary" onClick={() => setIsShiftModalOpen(false)}>
                                            Cancel
                                        </button>
                                        <button type="button" className="bs-btn bs-btn-primary" onClick={handleShiftChange}>
                                            Done
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* 2. Cycles List */}
                    <div className="plan-tour-body">
                        <div className="tour-cycle-card-panel-lists">
                            {/* A. Completed Cycles Cards */}
                            {state.completedCycles.map((rec, cIdx) => {
                                const isExpanded = expandedCycle === rec.cycle;
                                const status = rec.status || (rec.hasDeviations ? "Pending Production Action" : "Completed");
                                const deviations = (rec.checkpoints || []).filter(cp => cp.criteria === "Not Okay");

                                let badgeColor = "#64748b";
                                let badgeBg = "#f1f5f9";
                                if (status === "Completed") { badgeColor = "#16a34a"; badgeBg = "#dcfce7"; }
                                else if (status === "Pending Production Action") { badgeColor = "#ea580c"; badgeBg = "#ffedd5"; }
                                else if (status === "Pending QA Re-Verification") { badgeColor = "#7c3aed"; badgeBg = "#ede9fe"; }
                                else if (status === "Escalated" || status === "Not Operational") { badgeColor = "#dc2626"; badgeBg = "#fee2e2"; }

                                return (
                                    <div
                                        key={cIdx}
                                        className={`bs-card bs-card-secondary tour-cycle-panel ${isExpanded ? 'bs-card-toggler-is-active' : ''}`}
                                    >
                                        <div
                                            className="bs-card-header"
                                            onClick={() => setExpandedCycle(isExpanded ? null : rec.cycle)}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                <h4 className="bs-card-title" style={{ margin: 0 }}>{rec.cycle}</h4>
                                                <span
                                                    className="badge"
                                                    style={{ backgroundColor: badgeBg, color: badgeColor, fontWeight: 600, padding: '4px 8px', borderRadius: 4, fontSize: 11 }}
                                                >
                                                    {status}
                                                </span>
                                                {!!rec.photoFileNames?.length && (
                                                    <span style={{ fontSize: 12, color: '#2563eb', fontWeight: 600 }}>
                                                        {rec.photoFileNames.length} photo{rec.photoFileNames.length === 1 ? '' : 's'}
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                <span className="tour-date" style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
                                                    {rec.cycleDate ? moment(rec.cycleDate).format("DD/MM/YYYY") : moment().format("DD/MM/YYYY")}
                                                </span>
                                                <button type="button" className="bs-btn icon-btn bs-card-toggler-btn">
                                                    <svg
                                                        style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
                                                        xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"
                                                    >
                                                        <path d="M3.66667 5.66666L8.33333 10.3333L13 5.66666" stroke="#0C0D10" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>

                                        {isExpanded && (
                                            <div className="bs-card-body">
                                                {renderStepper(status)}

                                                <div className="tour-cycle-info-wrapper" style={{ marginBottom: 15 }}>
                                                    <div className="tour-cyle-start-info">
                                                        <div><strong>Product:</strong> {rec.productName || "N/A"}</div>
                                                        <div><strong>Executive:</strong> {rec.executiveName || "N/A"}</div>
                                                        <div><strong>Line:</strong> {rec.location || line}</div>
                                                        <div><strong>Response:</strong> {rec.acceptanceResponse || "OK"}</div>
                                                    </div>
                                                </div>

                                                <div className="tour-cyle-step-completed">
                                                    <h5 style={{ marginTop: 0, fontWeight: 'bold', color: '#1e293b' }}>
                                                        CCP & OPRP Checklist Summary
                                                    </h5>
                                                    {renderMatrixSummaryTable(rec.checkpoints || [])}
                                                </div>

                                                {/* Deviation Workflow Panel */}
                                                {deviations.length > 0 && status !== "Completed" && (
                                                    <div className="deviation-workflow-panel">
                                                        <h5 style={{ margin: '0 0 12px 0', fontWeight: 'bold', color: '#c2410c' }}>
                                                            ⚠️ Quality Deviation Registered
                                                        </h5>

                                                        {deviations.map((dev, dIdx) => {
                                                            const devKey = `${rec.cycle}_${dev.checkpointName}`;
                                                            const isClosed = dev.deviationStatus === "Closed";

                                                            return (
                                                                <div key={dIdx} className="deviation-item">
                                                                    <div style={{ fontWeight: 'bold', color: '#1e293b', fontSize: 13, marginBottom: 4 }}>
                                                                        {dev.checkpointName}
                                                                    </div>
                                                                    <div style={{ fontSize: 12, color: '#dc2626', background: '#fef2f2', padding: 8, borderRadius: 6, marginBottom: 8 }}>
                                                                        <strong>QA Remarks:</strong> {dev.defectRemarks || "Deviation recorded"}
                                                                    </div>

                                                                    {status === "Pending Production Action" && (
                                                                        <div style={{ marginTop: 8 }}>
                                                                            <label className="form-label" style={{ fontSize: 12 }}>
                                                                                Corrective Action Plan (Production)
                                                                            </label>
                                                                            <input
                                                                                type="text"
                                                                                className="form-control"
                                                                                placeholder="Describe corrective actions taken..."
                                                                                value={actionPlans[devKey] || ""}
                                                                                onChange={e => setActionPlans(prev => ({ ...prev, [devKey]: e.target.value }))}
                                                                            />
                                                                        </div>
                                                                    )}

                                                                    {status === "Pending QA Re-Verification" && (
                                                                        <div style={{ marginTop: 8 }}>
                                                                            <div style={{ fontSize: 12, color: '#334155', marginBottom: 6 }}>
                                                                                <strong>Production Action:</strong> {dev.actionTaken || "Action taken"}
                                                                            </div>
                                                                            <label className="form-label" style={{ fontSize: 12 }}>
                                                                                QA Re-verification Comment
                                                                            </label>
                                                                            <input
                                                                                type="text"
                                                                                className="form-control"
                                                                                placeholder="Enter QA verification remarks..."
                                                                                value={qaComments[devKey] || ""}
                                                                                onChange={e => setQaComments(prev => ({ ...prev, [devKey]: e.target.value }))}
                                                                            />
                                                                        </div>
                                                                    )}

                                                                    {isClosed && (
                                                                        <div style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>
                                                                            ✓ Resolved & Closed
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}

                                                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 12 }}>
                                                            {status === "Pending Production Action" && (
                                                                <button
                                                                    type="button"
                                                                    disabled={isSubmitting}
                                                                    onClick={() => handleSubmitCorrectiveActions(rec.cycle, rec)}
                                                                    className="bs-btn bs-btn-primary"
                                                                >
                                                                    Submit Corrective Actions
                                                                </button>
                                                            )}

                                                            {status === "Pending QA Re-Verification" && (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                        disabled={isSubmitting}
                                                                        onClick={() => handleQAClosure(rec.cycle, rec, false)}
                                                                        className="bs-btn bs-btn-outline-primary"
                                                                    >
                                                                        Reject Actions
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        disabled={isSubmitting}
                                                                        onClick={() => handleQAClosure(rec.cycle, rec, true)}
                                                                        className="bs-btn bs-btn-primary"
                                                                        style={{ backgroundColor: '#16a34a', borderColor: '#16a34a' }}
                                                                    >
                                                                        Approve & Close Cycle
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            {/* B. Active Cycle Card */}
                            <div className="bs-card bs-card-secondary tour-cycle-panel bs-card-toggler-is-active" id={`cycle-${currentCycle}`}>
                                <div className="bs-card-header">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <h4 className="bs-card-title" style={{ margin: 0 }}>{currentCycle}</h4>
                                        <span
                                            className="badge"
                                            style={{
                                                backgroundColor: isSessionStarted ? '#fef3c7' : '#dbeafe',
                                                color: isSessionStarted ? '#d97706' : '#2563eb',
                                                fontWeight: 600,
                                                padding: '4px 8px',
                                                borderRadius: 4,
                                                fontSize: 11
                                            }}
                                        >
                                            {isSessionStarted ? "Checklist Filling" : "Metadata Setup"}
                                        </span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <span className="tour-date" style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
                                            {moment().format("DD/MM/YYYY")}
                                        </span>
                                        <button type="button" className="bs-btn icon-btn bs-card-toggler-btn">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                <path d="M3.66667 5.66666L8.33333 10.3333L13 5.66666" stroke="#0C0D10" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>

                                <div className="bs-card-body" style={{ display: 'block' }}>
                                    {renderStepper(isSessionStarted ? "Checklist Filling" : "Metadata Setup")}

                                    {!isSessionStarted ? (
                                        <div className="tour-cyle-step-start">
                                            <div className="form-group">
                                                <label className="form-label">Acceptance Response</label>
                                                <div className="acceptance-toggles">
                                                    {(["OK", "Line Not Operational", "Plant Not Operational"] as const).map(opt => (
                                                        <button
                                                            key={opt}
                                                            type="button"
                                                            className={`bs-btn bs-btn-outline-primary ${acceptanceResponse === opt ? 'active' : ''}`}
                                                            onClick={() => setAcceptanceResponse(opt)}
                                                        >
                                                            {opt}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            {acceptanceResponse !== "OK" ? (
                                                <div className="form-group" style={{ marginTop: 15 }}>
                                                    <label className="form-label">Reason for Shutdown / Closure</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="Enter reason here..."
                                                        value={shutdownReason}
                                                        onChange={e => setShutdownReason(e.target.value)}
                                                    />
                                                    <div style={{ marginTop: 15 }}>
                                                        <button
                                                            type="button"
                                                            disabled={isSubmitting}
                                                            className="bs-btn bs-btn-primary"
                                                            onClick={handleSaveShutdown}
                                                        >
                                                            Record Shutdown Closure
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div id="session-metadata-fields" style={{ marginTop: 15 }}>
                                                    <div className="form-group">
                                                        <label className="form-label">Product Name</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Enter Product Name..."
                                                            value={productName}
                                                            onChange={e => setProductName(e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label className="form-label">Production Executive</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Enter Production Executive Name..."
                                                            value={executiveName}
                                                            onChange={e => setExecutiveName(e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label className="form-label">Line No</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={line}
                                                            disabled
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label className="form-label">Session Time</label>
                                                        <input
                                                            type="time"
                                                            className="form-control"
                                                            value={sessionTime}
                                                            onChange={e => setSessionTime(e.target.value)}
                                                        />
                                                    </div>
                                                    <div style={{ marginTop: 15 }}>
                                                        <button
                                                            type="button"
                                                            className="bs-btn bs-btn-primary"
                                                            onClick={() => {
                                                                if (!productName.trim() || !executiveName.trim()) {
                                                                    alert("Please fill in Product Name and Production Executive fields.");
                                                                    return;
                                                                }
                                                                setIsSessionStarted(true);
                                                            }}
                                                        >
                                                            Start Session
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="tour-cyle-step-form">
                                            {/* Top Metadata Info Bar */}
                                            <div className="tour-cycle-info-wrapper" style={{ marginBottom: 15 }}>
                                                <div className="tour-cyle-start-info" style={{ display: 'flex', flexWrap: 'wrap', gap: 15, background: '#f8fafc', padding: 12, borderRadius: 8 }}>
                                                    <div><strong>Product:</strong> {productName}</div>
                                                    <div><strong>Executive Name:</strong> {executiveName}</div>
                                                    <div><strong>Line No:</strong> {line}</div>
                                                    <div><strong>Session Time:</strong> {sessionTime}</div>
                                                    <div><strong>Response:</strong> OK</div>
                                                </div>
                                            </div>

                                            {/* Sub-Tab Navigation Header */}
                                            <div className="tabs-header-container">
                                                {checkpoints.map((cp, idx) => {
                                                    let cleanName = cp;
                                                    if (cp.includes(":")) {
                                                        cleanName = cp.split(":")[0].trim();
                                                    } else if (cp.includes("- Post Cooling")) {
                                                        cleanName = "CCP (Post Cooling)";
                                                    } else if (cp.includes("(Metal Detector)")) {
                                                        cleanName = cp.replace("(Metal Detector)", "").trim();
                                                    }
                                                    return (
                                                        <button
                                                            key={idx}
                                                            type="button"
                                                            className={`tab-header-btn ${activeTabIdx === idx ? 'active' : ''}`}
                                                            onClick={() => setActiveTabIdx(idx)}
                                                        >
                                                            {cleanName}
                                                        </button>
                                                    );
                                                })}
                                            </div>

                                            {/* Active Tab Panes */}
                                            <div className="tab-pane-content" style={{ display: 'block' }}>
                                                <div className="rows-container">
                                                    {activeRows.map((row, rIdx) => (
                                                        <div
                                                            key={row.rowNumber}
                                                            className="bs-card bs-card-light bs-card-sm checkpoint-inner-row"
                                                        >
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, borderBottom: '1px solid #e2e8f0', paddingBottom: 8 }}>
                                                                <strong style={{ color: '#475569' }}>Row {row.rowNumber}</strong>
                                                                {activeRows.length > 1 && (
                                                                    <button
                                                                        type="button"
                                                                        className="bs-btn btn-sm btn-link"
                                                                        style={{ color: '#ef4444' }}
                                                                        onClick={() => handleRemoveRow(activeTabIdx, rIdx)}
                                                                    >
                                                                        Remove
                                                                    </button>
                                                                )}
                                                            </div>

                                                            {/* Column Metadata display */}
                                                            <div style={{ display: 'flex', gap: 10, marginBottom: 15, background: '#f1f5f9', padding: 10, borderRadius: 6, flexWrap: 'wrap' }}>
                                                                <div style={{ fontSize: 12 }}><strong>Product:</strong> {productName}</div>
                                                                <div style={{ fontSize: 12 }}><strong>Exec:</strong> {executiveName}</div>
                                                                <div style={{ fontSize: 12 }}><strong>Line:</strong> {line}</div>
                                                            </div>

                                                            {/* Checkpoints list */}
                                                            <div className="checkpoints-block-list">
                                                                {/* FE Checkpoints */}
                                                                <div style={{ marginBottom: 15 }}>
                                                                    <h6 style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: '#1e293b' }}>FE (Ferrous) Check</h6>
                                                                    {["fecentrepass1", "fecentrepass2"].map(k => {
                                                                        const scDef = DEFAULT_SUB_CHECKS.find(s => s.key === k)!;
                                                                        const subState = row.subChecks[k] || { criteria: "Okay", sensitivityValue: "", defectRemarks: "" };
                                                                        const isNotOk = subState.criteria === "Not Okay";

                                                                        return (
                                                                            <div key={k} className={`checkpoint-check-row ${isNotOk ? 'is-not-ok' : ''}`}>
                                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                                    <span className="check-label" style={{ fontSize: 13, fontWeight: 500, color: '#334155' }}>
                                                                                        {scDef.label}
                                                                                    </span>
                                                                                    <div className="toggle-switch-badge">
                                                                                        <button
                                                                                            type="button"
                                                                                            className={`badge badge-lg badge-error ${isNotOk ? 'badge-fill' : ''}`}
                                                                                            onClick={() => handleSubCheckChange(activeTabIdx, rIdx, k, { criteria: "Not Okay" })}
                                                                                        >
                                                                                            Not Okay
                                                                                        </button>
                                                                                        <button
                                                                                            type="button"
                                                                                            className={`badge badge-lg badge-success ${!isNotOk ? 'badge-fill' : ''}`}
                                                                                            onClick={() => handleSubCheckChange(activeTabIdx, rIdx, k, { criteria: "Okay", defectRemarks: "" })}
                                                                                        >
                                                                                            Okay
                                                                                        </button>
                                                                                    </div>
                                                                                </div>

                                                                                {isNotOk && (
                                                                                    <div className="defect-remarks-field" style={{ display: 'block', marginTop: 10, borderTop: '1px dashed #cbd5e1', paddingTop: 8 }}>
                                                                                        <label className="form-label" style={{ fontSize: 11, color: '#475569', marginBottom: 2 }}>
                                                                                            Defect Remarks
                                                                                        </label>
                                                                                        <input
                                                                                            type="text"
                                                                                            className="form-control check-remarks-input"
                                                                                            placeholder="Enter remarks..."
                                                                                            style={{ height: 32 }}
                                                                                            value={subState.defectRemarks || ""}
                                                                                            onChange={e => handleSubCheckChange(activeTabIdx, rIdx, k, { defectRemarks: e.target.value })}
                                                                                        />
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>

                                                                {/* NFE Checkpoints */}
                                                                <div style={{ marginBottom: 15 }}>
                                                                    <h6 style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: '#1e293b' }}>NFE (Non-Ferrous) Check</h6>
                                                                    {["nfecentrepass1", "nfecentrepass2"].map(k => {
                                                                        const scDef = DEFAULT_SUB_CHECKS.find(s => s.key === k)!;
                                                                        const subState = row.subChecks[k] || { criteria: "Okay", sensitivityValue: "", defectRemarks: "" };
                                                                        const isNotOk = subState.criteria === "Not Okay";

                                                                        return (
                                                                            <div key={k} className={`checkpoint-check-row ${isNotOk ? 'is-not-ok' : ''}`}>
                                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                                    <span className="check-label" style={{ fontSize: 13, fontWeight: 500, color: '#334155' }}>
                                                                                        {scDef.label}
                                                                                    </span>
                                                                                    <div className="toggle-switch-badge">
                                                                                        <button
                                                                                            type="button"
                                                                                            className={`badge badge-lg badge-error ${isNotOk ? 'badge-fill' : ''}`}
                                                                                            onClick={() => handleSubCheckChange(activeTabIdx, rIdx, k, { criteria: "Not Okay" })}
                                                                                        >
                                                                                            Not Okay
                                                                                        </button>
                                                                                        <button
                                                                                            type="button"
                                                                                            className={`badge badge-lg badge-success ${!isNotOk ? 'badge-fill' : ''}`}
                                                                                            onClick={() => handleSubCheckChange(activeTabIdx, rIdx, k, { criteria: "Okay", defectRemarks: "" })}
                                                                                        >
                                                                                            Okay
                                                                                        </button>
                                                                                    </div>
                                                                                </div>

                                                                                {isNotOk && (
                                                                                    <div className="defect-remarks-field" style={{ display: 'block', marginTop: 10, borderTop: '1px dashed #cbd5e1', paddingTop: 8 }}>
                                                                                        <label className="form-label" style={{ fontSize: 11, color: '#475569', marginBottom: 2 }}>
                                                                                            Defect Remarks
                                                                                        </label>
                                                                                        <input
                                                                                            type="text"
                                                                                            className="form-control check-remarks-input"
                                                                                            placeholder="Enter remarks..."
                                                                                            style={{ height: 32 }}
                                                                                            value={subState.defectRemarks || ""}
                                                                                            onChange={e => handleSubCheckChange(activeTabIdx, rIdx, k, { defectRemarks: e.target.value })}
                                                                                        />
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>

                                                                {/* SS Checkpoints */}
                                                                <div style={{ marginBottom: 15 }}>
                                                                    <h6 style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: '#1e293b' }}>SS (Stainless Steel) Check</h6>
                                                                    {["sscentrepass1", "sscentrepass2"].map(k => {
                                                                        const scDef = DEFAULT_SUB_CHECKS.find(s => s.key === k)!;
                                                                        const subState = row.subChecks[k] || { criteria: "Okay", sensitivityValue: "", defectRemarks: "" };
                                                                        const isNotOk = subState.criteria === "Not Okay";

                                                                        return (
                                                                            <div key={k} className={`checkpoint-check-row ${isNotOk ? 'is-not-ok' : ''}`}>
                                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                                    <span className="check-label" style={{ fontSize: 13, fontWeight: 500, color: '#334155' }}>
                                                                                        {scDef.label}
                                                                                    </span>
                                                                                    <div className="toggle-switch-badge">
                                                                                        <button
                                                                                            type="button"
                                                                                            className={`badge badge-lg badge-error ${isNotOk ? 'badge-fill' : ''}`}
                                                                                            onClick={() => handleSubCheckChange(activeTabIdx, rIdx, k, { criteria: "Not Okay" })}
                                                                                        >
                                                                                            Not Okay
                                                                                        </button>
                                                                                        <button
                                                                                            type="button"
                                                                                            className={`badge badge-lg badge-success ${!isNotOk ? 'badge-fill' : ''}`}
                                                                                            onClick={() => handleSubCheckChange(activeTabIdx, rIdx, k, { criteria: "Okay", defectRemarks: "" })}
                                                                                        >
                                                                                            Okay
                                                                                        </button>
                                                                                    </div>
                                                                                </div>

                                                                                {isNotOk && (
                                                                                    <div className="defect-remarks-field" style={{ display: 'block', marginTop: 10, borderTop: '1px dashed #cbd5e1', paddingTop: 8 }}>
                                                                                        <label className="form-label" style={{ fontSize: 11, color: '#475569', marginBottom: 2 }}>
                                                                                            Defect Remarks
                                                                                        </label>
                                                                                        <input
                                                                                            type="text"
                                                                                            className="form-control check-remarks-input"
                                                                                            placeholder="Enter remarks..."
                                                                                            style={{ height: 32 }}
                                                                                            value={subState.defectRemarks || ""}
                                                                                            onChange={e => handleSubCheckChange(activeTabIdx, rIdx, k, { defectRemarks: e.target.value })}
                                                                                        />
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>

                                                                {/* MD Sensitivity Check */}
                                                                <div>
                                                                    <h6 style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: '#1e293b' }}>M.D. Sensitivity & Rejection in Time</h6>
                                                                    {["mdsensitivity"].map(k => {
                                                                        const scDef = DEFAULT_SUB_CHECKS.find(s => s.key === k)!;
                                                                        const subState = row.subChecks[k] || { criteria: "Okay", sensitivityValue: "Fe: 1.5mm / NFe: 1.5mm / SS: 2.0mm", defectRemarks: "" };
                                                                        const isNotOk = subState.criteria === "Not Okay";

                                                                        return (
                                                                            <div key={k} className={`checkpoint-check-row ${isNotOk ? 'is-not-ok' : ''}`}>
                                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                                    <span className="check-label" style={{ fontSize: 13, fontWeight: 500, color: '#334155' }}>
                                                                                        {scDef.label}
                                                                                    </span>
                                                                                    <div className="toggle-switch-badge">
                                                                                        <button
                                                                                            type="button"
                                                                                            className={`badge badge-lg badge-error ${isNotOk ? 'badge-fill' : ''}`}
                                                                                            onClick={() => handleSubCheckChange(activeTabIdx, rIdx, k, { criteria: "Not Okay" })}
                                                                                        >
                                                                                            Not Okay
                                                                                        </button>
                                                                                        <button
                                                                                            type="button"
                                                                                            className={`badge badge-lg badge-success ${!isNotOk ? 'badge-fill' : ''}`}
                                                                                            onClick={() => handleSubCheckChange(activeTabIdx, rIdx, k, { criteria: "Okay", defectRemarks: "" })}
                                                                                        >
                                                                                            Okay
                                                                                        </button>
                                                                                    </div>
                                                                                </div>

                                                                                <div style={{ marginTop: 8 }}>
                                                                                    <label className="form-label" style={{ fontSize: 11, color: '#64748b' }}>
                                                                                        Sensitivity Value (e.g. 1.2mm)
                                                                                    </label>
                                                                                    <input
                                                                                        type="text"
                                                                                        className="form-control check-val-input"
                                                                                        placeholder="Enter sensitivity value..."
                                                                                        style={{ height: 32 }}
                                                                                        value={subState.sensitivityValue || ""}
                                                                                        onChange={e => handleSubCheckChange(activeTabIdx, rIdx, k, { sensitivityValue: e.target.value })}
                                                                                    />
                                                                                </div>

                                                                                {isNotOk && (
                                                                                    <div className="defect-remarks-field" style={{ display: 'block', marginTop: 10, borderTop: '1px dashed #cbd5e1', paddingTop: 8 }}>
                                                                                        <label className="form-label" style={{ fontSize: 11, color: '#475569', marginBottom: 2 }}>
                                                                                            Defect Remarks
                                                                                        </label>
                                                                                        <input
                                                                                            type="text"
                                                                                            className="form-control check-remarks-input"
                                                                                            placeholder="Enter remarks..."
                                                                                            style={{ height: 32 }}
                                                                                            value={subState.defectRemarks || ""}
                                                                                            onChange={e => handleSubCheckChange(activeTabIdx, rIdx, k, { defectRemarks: e.target.value })}
                                                                                        />
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Add Row Button */}
                                                <div style={{ marginTop: 15, marginBottom: 10 }}>
                                                    <button
                                                        type="button"
                                                        className="bs-btn bs-btn-outline-primary btn-sm"
                                                        onClick={() => handleAddRow(activeTabIdx)}
                                                    >
                                                        + Add Row
                                                    </button>
                                                </div>

                                                <PhotoAttachField
                                                    photos={photos}
                                                    onChange={setPhotos}
                                                    helperText="Take photo opens the camera directly for this CCP and OPRP cycle."
                                                />
                                            </div>

                                            {/* Footer Actions */}
                                            <div className="form-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20, borderTop: '1px solid #e2e8f0', paddingTop: 15 }}>
                                                <button
                                                    type="button"
                                                    className="bs-btn bs-btn-outline-primary"
                                                    onClick={() => setIsSessionStarted(false)}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type="button"
                                                    disabled={isSubmitting}
                                                    className="bs-btn bs-btn-secondary"
                                                    style={{ backgroundColor: '#64748b', color: '#ffffff', border: 'none' }}
                                                    onClick={() => handleSaveSession(true)}
                                                >
                                                    Pause
                                                </button>
                                                <button
                                                    type="button"
                                                    disabled={isSubmitting}
                                                    className="bs-btn bs-btn-success"
                                                    style={{ backgroundColor: '#10b981', color: '#ffffff', border: 'none', fontWeight: 700, padding: '10px 28px', borderRadius: 8, boxShadow: '0 4px 6px -1px rgba(16, 185, 129, 0.2)' }}
                                                    onClick={() => handleSaveSession(false)}
                                                >
                                                    <i className="fa fa-check-circle me-1"></i> Complete Tour
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Finish Tour Action */}
                    {state.completedCycles.length > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
                            <button
                                type="button"
                                onClick={() => dispatch({ type: 'SET_SCREEN', payload: CCP_SCREENS.SUMMARY })}
                                className="bs-btn bs-btn-primary"
                                style={{ backgroundColor: '#16a34a', borderColor: '#16a34a', padding: '10px 22px', fontSize: 14 }}
                            >
                                View Summary & Finish Tour ({state.completedCycles.length} Cycles Completed)
                            </button>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default CCPOPRPChecklist;
